import { useEffect, useState } from "react";
import type { GigListItemResponse, GigRequestRequest } from "../../types/gigTypes";
import { formatEstimatedDuration } from "../../utils/DateUtils";
import { formatGigScheduleSummary } from "../../utils/GigScheduleUtils";
import { formatRoundedGigCompensation } from "../../utils/GigCompensationUtils";
import { formatGigCrewSummary } from "../../utils/GigCrewUtils";
import { formatCityStateZip, formatDistanceLabel, formatGigListLocation } from "../../utils/LocationUtils";
import { tableColumns } from "./columns";
import GigDetailGrid from "./GigDetailGrid";
import Pagination from "./Pagination";
import RequestGigModal from "./request";
import TableFilters, { type DistanceFilter, type PaySortOption, type TimeFrameFilter } from "./TableFilters";
import TableRow from "./TableRow";
import ResponsiveDataList from "../ui/DataTable/ResponsiveDataList";
import TableContainer from "../ui/DataTable/TableContainer";
import TableHeader from "../ui/DataTable/TableHeader";
import TableRowSkeleton from "../ui/DataTable/TableRowSkeleton";
import Modal from "../ui/Modal/Modal";

type RequestFeedback = {
  tone: "success" | "error";
  message: string;
};

type GigsTableProps = {
  gigs: GigListItemResponse[];
  autoExpandGigId?: string | null;
  currentUserId?: string | null;
  currentUserZip?: string | null;
  canRequestAsTeen: boolean;
  isLoading: boolean;
  search: string;
  showFilters?: boolean;
  timeFrame: TimeFrameFilter;
  distance: DistanceFilter;
  customFromDate: string;
  customToDate: string;
  pageSize: number;
  paySort: PaySortOption;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onTimeFrameChange: (value: TimeFrameFilter) => void;
  onDistanceChange: (value: DistanceFilter) => void;
  onCustomFromDateChange: (value: string) => void;
  onCustomToDateChange: (value: string) => void;
  onPaySortChange: (value: PaySortOption) => void;
  onPageChange: (nextPage: number) => void;
  onRequestGig: (gigId: string, payload: GigRequestRequest) => Promise<void>;
  onBeforeRequestGig?: (gig: GigListItemResponse) => boolean;
  deepLinkedGig?: GigListItemResponse | null;
  emptyMessage?: string;
};

const isMobileBoardViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(max-width: 1023px)").matches;

const GigsTable = ({
  gigs,
  autoExpandGigId,
  currentUserId,
  currentUserZip,
  canRequestAsTeen,
  isLoading,
  search,
  showFilters = true,
  timeFrame,
  distance,
  customFromDate,
  customToDate,
  pageSize,
  paySort,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onTimeFrameChange,
  onDistanceChange,
  onCustomFromDateChange,
  onCustomToDateChange,
  onPaySortChange,
  onPageChange,
  onRequestGig,
  onBeforeRequestGig,
  deepLinkedGig = null,
  emptyMessage = "No gigs found.",
}: GigsTableProps) => {
  const [expandedGigId, setExpandedGigId] = useState<string | null>(null);
  const [handledAutoExpandGigId, setHandledAutoExpandGigId] = useState<string | null>(null);
  const [requestingGigId, setRequestingGigId] = useState<string | null>(null);
  const [requestedGigIds, setRequestedGigIds] = useState<Record<string, true>>({});
  const [feedbackByGigId, setFeedbackByGigId] = useState<Record<string, RequestFeedback>>({});
  const [requestModalGig, setRequestModalGig] = useState<GigListItemResponse | null>(null);
  const [mobileDetailGigId, setMobileDetailGigId] = useState<string | null>(null);

  const toggleExpanded = (gigId: string) => {
    setExpandedGigId((current) => (current === gigId ? null : gigId));
  };

  const handleRequestGig = async (gigId: string, payload: GigRequestRequest) => {
    setRequestingGigId(gigId);
    setFeedbackByGigId((current) => {
      const next = { ...current };
      delete next[gigId];
      return next;
    });

    try {
      await onRequestGig(gigId, payload);
      setRequestedGigIds((current) => ({ ...current, [gigId]: true }));
      setFeedbackByGigId((current) => ({
        ...current,
        [gigId]: { tone: "success", message: "Application submitted." },
      }));
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim().length > 0 ? error.message : "Unable to submit application.";
      setFeedbackByGigId((current) => ({ ...current, [gigId]: { tone: "error", message } }));
      throw error;
    } finally {
      setRequestingGigId(null);
    }
  };

  const openRequestModal = (gig: GigListItemResponse) => {
    if (onBeforeRequestGig && !onBeforeRequestGig(gig)) {
      return;
    }
    setRequestModalGig(gig);
  };

  const closeRequestModal = () => {
    setRequestModalGig(null);
  };

  const onSubmitRequest = async (payload: GigRequestRequest) => {
    if (!requestModalGig) {
      return;
    }

    await handleRequestGig(requestModalGig.id, payload);
    closeRequestModal();
  };

  const showNoResults = !isLoading && gigs.length === 0;
  const loadingRows = Array.from({ length: 5 });

  useEffect(() => {
    if (!autoExpandGigId || handledAutoExpandGigId === autoExpandGigId) {
      return;
    }

    const matchingGig = gigs.find((gig) => gig.id === autoExpandGigId);
    const matchingDeepLinkedGig = deepLinkedGig?.id === autoExpandGigId ? deepLinkedGig : null;
    if (!matchingGig && !matchingDeepLinkedGig) {
      return;
    }

    if (isMobileBoardViewport() || !matchingGig) {
      setMobileDetailGigId(autoExpandGigId);
    } else {
      setExpandedGigId(autoExpandGigId);
    }
    setHandledAutoExpandGigId(autoExpandGigId);
  }, [autoExpandGigId, deepLinkedGig, gigs, handledAutoExpandGigId]);

  const renderScheduleSummary = (gig: GigListItemResponse) => {
    return formatGigScheduleSummary(gig);
  };

  const mobileDetailGig = mobileDetailGigId
    ? gigs.find((gig) => gig.id === mobileDetailGigId) ?? (deepLinkedGig?.id === mobileDetailGigId ? deepLinkedGig : null)
    : null;

  return (
    <section className="mx-[calc(50%_-_50vw)] mt-1 w-screen px-2 sm:mx-0 sm:mt-5 sm:w-auto sm:px-0 md:mt-6">
      {showFilters ? (
        <TableFilters
          search={search}
          timeFrame={timeFrame}
          distance={distance}
          customFromDate={customFromDate}
          customToDate={customToDate}
          paySort={paySort}
          onSearchChange={onSearchChange}
          onTimeFrameChange={onTimeFrameChange}
          onDistanceChange={onDistanceChange}
          onCustomFromDateChange={onCustomFromDateChange}
          onCustomToDateChange={onCustomToDateChange}
          onPaySortChange={onPaySortChange}
        />
      ) : null}

      {isLoading ? (
        <>
          <div className="grid gap-4 lg:hidden">
            {loadingRows.map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm"
              >
                <div className="grid animate-pulse grid-cols-[minmax(0,1fr)_4.5rem] gap-x-1 gap-y-2">
                  <div className="col-span-2 h-4 w-3/4 rounded-full bg-myteengig-background" />
                  <div className="h-3 w-full rounded-full bg-myteengig-background" />
                  <div className="ml-auto h-4 w-16 rounded-full bg-myteengig-background" />
                  <div className="h-3 w-1/2 rounded-full bg-myteengig-background" />
                  <div className="ml-auto h-3 w-10 rounded-full bg-myteengig-background" />
                </div>
              </div>
            ))}
          </div>

          <TableContainer className="hidden lg:block">
            <table className="min-w-full divide-y divide-myteengig-border">
              <TableHeader columns={tableColumns} />
              <tbody className="divide-y divide-myteengig-border">
                {loadingRows.map((_, index) => (
                  <TableRowSkeleton key={index} columnCount={tableColumns.length} />
                ))}
              </tbody>
            </table>
          </TableContainer>
        </>
      ) : null}

      {showNoResults ? (
        <>
          <div className="rounded-[1.5rem] border border-myteengig-border bg-white px-5 py-6 text-sm text-myteengig-text-muted shadow-sm lg:hidden">
            {emptyMessage}
          </div>

          <TableContainer className="hidden lg:block">
            <table className="min-w-full divide-y divide-myteengig-border">
              <TableHeader columns={tableColumns} />
              <tbody className="divide-y divide-myteengig-border">
                <tr>
                  <td className="px-4 py-6 text-sm text-myteengig-text-muted" colSpan={tableColumns.length}>
                    {emptyMessage}
                  </td>
                </tr>
              </tbody>
            </table>
          </TableContainer>
        </>
      ) : null}

      {!isLoading && !showNoResults ? (
        <ResponsiveDataList
          items={gigs}
          columns={tableColumns}
          getItemKey={(gig) => gig.id}
          mobileClassName="gap-2"
          renderMobileCard={(gig) => {
            const compensationLabel = formatRoundedGigCompensation(gig.paymentAmountCents, gig.volunteer);
            const durationLabel = formatEstimatedDuration(gig.estimatedDurationMinutes);
            const locationLabel = formatCityStateZip(gig);
            const distanceLabel = formatDistanceLabel({
              distanceMiles: gig.distanceMiles,
              zip: gig.zip,
              comparisonZip: currentUserZip,
            });
            const crewSummary = formatGigCrewSummary(gig);

            return (
              <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <button
                  type="button"
                  className="grid w-full grid-cols-[minmax(0,1fr)_4.5rem] gap-x-1 gap-y-0.5 px-2 py-3 text-left transition hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-myteengig-primary"
                  onClick={() => setMobileDetailGigId(gig.id)}
                  aria-label={`View details for ${gig.title}`}
                >
                  <span className="col-span-2 block truncate text-base font-semibold leading-5 text-myteengig-text-primary">
                    {gig.title}
                  </span>
                  <span className="min-w-0 text-[0.82rem] leading-4 text-myteengig-text-muted">
                    {locationLabel}
                  </span>
                  <span className="min-w-0 truncate text-right text-[0.9rem] font-bold leading-4 text-emerald-700">
                    {compensationLabel}
                  </span>
                  <span className="min-w-0 text-[0.78rem] font-medium leading-4 text-myteengig-text-muted">
                    {distanceLabel ?? ""}
                  </span>
                  <span className="min-w-0 truncate text-right text-[0.78rem] font-semibold leading-4 text-myteengig-text-muted">
                    {durationLabel}
                  </span>
                  <span className="col-span-2 min-w-0 truncate text-[0.78rem] font-medium leading-4 text-myteengig-text-muted">
                    {crewSummary}
                  </span>
                </button>
              </article>
            );
          }}
          renderDesktopRows={(gig) => (
            <TableRow
              key={gig.id}
              gig={gig}
              currentUserId={currentUserId}
              currentUserZip={currentUserZip}
              expanded={expandedGigId === gig.id}
              columnCount={tableColumns.length}
              canRequestAsTeen={canRequestAsTeen}
              isRequesting={requestingGigId === gig.id}
              hasRequested={Boolean(requestedGigIds[gig.id])}
              feedback={feedbackByGigId[gig.id] ?? null}
              onToggle={toggleExpanded}
              onRequestGig={openRequestModal}
            />
          )}
        />
      ) : null}

      <Pagination
        page={page}
        pageSize={pageSize}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        resultsCount={resultsCount}
        onPageChange={onPageChange}
      />
      <Modal
        isOpen={mobileDetailGig !== null}
        onClose={() => setMobileDetailGigId(null)}
        title={mobileDetailGig?.title ?? "Gig Details"}
        panelClassName="max-w-3xl"
      >
        {mobileDetailGig ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-myteengig-border bg-myteengig-background/60 px-4 py-3 text-left">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                {mobileDetailGig.category}
              </p>
              <p className="mt-1 text-sm font-medium text-myteengig-text-primary">
                {formatGigListLocation(mobileDetailGig, currentUserZip)}
              </p>
              <p className="mt-1 text-sm text-myteengig-text-muted">
                {renderScheduleSummary(mobileDetailGig)}
              </p>
              <p className="mt-1 text-sm font-medium text-myteengig-text-primary">
                {formatGigCrewSummary(mobileDetailGig)}
              </p>
            </div>

            <GigDetailGrid
              gig={mobileDetailGig}
              canRequestAsTeen={canRequestAsTeen}
              isOwnGig={Boolean(currentUserId && mobileDetailGig.posterUserId === currentUserId)}
              isRequesting={requestingGigId === mobileDetailGig.id}
              hasRequested={Boolean(requestedGigIds[mobileDetailGig.id])}
              feedback={feedbackByGigId[mobileDetailGig.id] ?? null}
              onRequestGig={() => openRequestModal(mobileDetailGig)}
            />
          </div>
        ) : null}
      </Modal>
      <RequestGigModal
        isOpen={requestModalGig !== null}
        gig={requestModalGig}
        isSubmitting={requestingGigId !== null}
        onClose={closeRequestModal}
        onSubmit={onSubmitRequest}
      />
    </section>
  );
};

export default GigsTable;
